import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section } from "@/components/PageHeader";
import UtilizationHeatmap from "@/components/UtilizationHeatmap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, UserPlus, Mail, Shuffle, Copy, CheckCircle2, AlertCircle, ExternalLink, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Team() {
  const { user: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [openInvite, setOpenInvite] = useState(false);
  const [openDirect, setOpenDirect] = useState(false);
  const [openReassign, setOpenReassign] = useState(false);
  const [reassignFrom, setReassignFrom] = useState(null);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "member" });
  const [inviteResult, setInviteResult] = useState(null);
  const [directForm, setDirectForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [reassignTo, setReassignTo] = useState("");
  const [heatmapKey, setHeatmapKey] = useState(0);
  const [editingCapacity, setEditingCapacity] = useState(null);
  const [capacityValue, setCapacityValue] = useState("");

  // Admin password reset
  const [resetTarget, setResetTarget] = useState(null); // member doc
  const [resetPassword, setResetPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const isAdmin = me?.role === "admin" || me?.role === "super_admin";
  const isSuperAdmin = me?.role === "super_admin";

  const load = async () => {
    const r = await api.get("/users");
    setMembers(r.data);
  };
  useEffect(() => { load(); }, []);

  const sendInvite = async () => {
    try {
      const r = await api.post("/users/invite", inviteForm);
      setInviteResult({ ...r.data, recipient_email: inviteForm.email, recipient_name: inviteForm.name });
      toast.success(r.data.email_sent ? "Invitation sent via email" : "Invite link generated — share it manually");
      setInviteForm({ name: "", email: "", role: "member" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createDirect = async () => {
    try {
      await api.post("/users", directForm);
      toast.success("Team member added");
      setOpenDirect(false);
      setDirectForm({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this team member?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (id, role) => {
    try {
      await api.put(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openReassignFor = (m) => {
    setReassignFrom(m);
    setReassignTo("");
    setOpenReassign(true);
  };

  const doReassign = async () => {
    if (!reassignTo) { toast.error("Pick a teammate"); return; }
    try {
      const r = await api.post("/tasks/bulk-reassign", { from_user_id: reassignFrom.id, to_user_id: reassignTo });
      toast.success(`Reassigned ${r.data.reassigned} tasks`);
      setOpenReassign(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startCapacityEdit = (m) => {
    setEditingCapacity(m.id);
    setCapacityValue(String(m.weekly_capacity_hours ?? 40));
  };

  const [editingCost, setEditingCost] = useState(null);
  const [costValue, setCostValue] = useState("");
  const startCostEdit = (m) => {
    setEditingCost(m.id);
    setCostValue(String(m.hourly_cost ?? 0));
  };
  const saveCost = async (id) => {
    const v = parseFloat(costValue);
    if (Number.isNaN(v) || v < 0) {
      toast.error("Enter a non-negative amount");
      return;
    }
    try {
      await api.put(`/users/${id}/hourly-cost`, { hourly_cost: v });
      toast.success("Hourly cost updated");
      setEditingCost(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveCapacity = async (id) => {
    const v = parseFloat(capacityValue);
    if (Number.isNaN(v) || v < 0 || v > 168) {
      toast.error("Enter 0-168 hours");
      return;
    }
    try {
      await api.put(`/users/${id}/capacity`, { weekly_capacity_hours: v });
      toast.success("Capacity updated");
      setEditingCapacity(null);
      setHeatmapKey(k => k + 1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const openReset = (m) => {
    setResetTarget(m);
    setResetPassword("");
  };

  const submitReset = async () => {
    if (!resetTarget) return;
    const pw = (resetPassword || "").trim();
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResetLoading(true);
    try {
      await api.put(`/users/${resetTarget.id}/password`, { new_password: pw });
      navigator.clipboard.writeText(pw).catch(() => {});
      toast.success(`Password reset. Share it with ${resetTarget.name || resetTarget.email}.`);
      setResetTarget(null);
      setResetPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setResetLoading(false);
    }
  };

  const mailtoLink = (email, name, link) => {
    const subject = encodeURIComponent("You're invited to Studio/OS");
    const body = encodeURIComponent(
      `Hi ${name || "there"},\n\nYou've been invited to join our Studio/OS workspace. Click the link below to set your password and sign in:\n\n${link}\n\nThis link expires in 7 days.\n\nThanks!`
    );
    return `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const roleStyle = (role) => role === "super_admin"
    ? "bg-blue-600 text-white border-blue-600"
    : role === "admin"
      ? "bg-neutral-900 text-white border-neutral-900"
      : "bg-neutral-50 border-neutral-200 text-neutral-700";

  return (
    <div data-testid="team-page">
      <PageHeader
        eyebrow="People"
        title="Team"
        description="Invite teammates, manage roles, and shift workload between people."
        testid="team-header"
        actions={isAdmin && (
          <>
            <Dialog open={openDirect} onOpenChange={setOpenDirect}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="add-direct-btn"><UserPlus size={14} className="mr-1" /> Add directly</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-sm">
                <DialogHeader><DialogTitle>Add team member directly</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input value={directForm.name} onChange={e => setDirectForm({ ...directForm, name: e.target.value })} data-testid="direct-name-input" /></div>
                  <div><Label>Email</Label><Input type="email" value={directForm.email} onChange={e => setDirectForm({ ...directForm, email: e.target.value })} data-testid="direct-email-input" /></div>
                  <div><Label>Initial password</Label><Input type="text" value={directForm.password} onChange={e => setDirectForm({ ...directForm, password: e.target.value })} placeholder="Min 6 characters" data-testid="direct-password-input" /></div>
                  <div>
                    <Label>Role</Label>
                    <Select value={directForm.role} onValueChange={v => setDirectForm({ ...directForm, role: v })}>
                      <SelectTrigger data-testid="direct-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                        {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createDirect} disabled={!directForm.name || !directForm.email || directForm.password.length < 6} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="direct-submit">Add</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={openInvite} onOpenChange={(v) => { setOpenInvite(v); if (!v) setInviteResult(null); }}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="send-invite-btn"><Mail size={14} className="mr-1" /> Send invite</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-sm">
                <DialogHeader><DialogTitle>Send email invitation</DialogTitle></DialogHeader>
                {!inviteResult ? (
                  <>
                    <div className="space-y-3">
                      <div><Label>Name</Label><Input value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} data-testid="invite-name-input" /></div>
                      <div><Label>Email</Label><Input type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email-input" /></div>
                      <div>
                        <Label>Role</Label>
                        <Select value={inviteForm.role} onValueChange={v => setInviteForm({ ...inviteForm, role: v })}>
                          <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Member</SelectItem>
                            {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                            {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 p-3 rounded-sm">
                        We'll send a one-time activation link to set their password. The link expires in 7 days.
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={sendInvite} disabled={!inviteForm.name || !inviteForm.email} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="invite-submit">Send invite</Button>
                    </DialogFooter>
                  </>
                ) : (
                  <div className="space-y-4" data-testid="invite-result">
                    {inviteResult.email_sent ? (
                      <div className="border border-emerald-200 bg-emerald-50 p-4 rounded-sm" data-testid="invite-result-success">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 size={18} className="text-emerald-700 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="font-display text-sm font-bold text-emerald-900 mb-1">Invitation delivered</div>
                            <div className="text-xs text-emerald-800 leading-relaxed">
                              Email is on its way to <span className="font-mono font-semibold">{inviteResult.recipient_email}</span>. Ask them to check their inbox (and spam folder). The link expires in 7 days.
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-amber-200 bg-amber-50 p-4 rounded-sm" data-testid="invite-result-fallback">
                        <div className="flex items-start gap-3">
                          <AlertCircle size={18} className="text-amber-700 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="font-display text-sm font-bold text-amber-900 mb-1">Email couldn't be auto-sent — invite still works</div>
                            <div className="text-xs text-amber-800 leading-relaxed mb-2">
                              Resend is currently in <span className="font-semibold">sandbox mode</span> (only delivers to your verified address). Verify a domain in <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline font-semibold">resend.com/domains</a> to send to anyone, or just share the link below — the invitation is fully active for 7 days.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-500">Activation link</Label>
                      <div className="flex gap-2 mt-1.5">
                        <Input value={inviteResult.invite_link} readOnly className="font-mono text-xs" data-testid="invite-link-input" onFocus={(e) => e.target.select()} />
                        <Button variant="outline" onClick={() => copyLink(inviteResult.invite_link)} className="rounded-sm shrink-0" data-testid="copy-invite-link" title="Copy link">
                          <Copy size={14} />
                        </Button>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-1.5">
                        One-time use · expires {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString("en", { month: "short", day: "numeric" })}
                      </div>
                    </div>

                    {!inviteResult.email_sent && inviteResult.recipient_email && (
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          onClick={() => copyLink(inviteResult.invite_link)}
                          className="rounded-sm"
                          data-testid="copy-link-big"
                        >
                          <Copy size={14} className="mr-1.5" /> Copy link
                        </Button>
                        <a
                          href={mailtoLink(inviteResult.recipient_email, inviteResult.recipient_name, inviteResult.invite_link)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center gap-1.5 border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 text-sm font-medium rounded-sm h-9 px-4 transition-colors"
                          data-testid="email-via-app"
                        >
                          <ExternalLink size={14} /> Email via your app
                        </a>
                      </div>
                    )}

                    <DialogFooter>
                      <Button onClick={() => { setOpenInvite(false); setInviteResult(null); }} className="rounded-sm bg-neutral-900 hover:bg-neutral-800 w-full sm:w-auto" data-testid="invite-done">Done</Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      />

      <Section>
        <UtilizationHeatmap refreshKey={heatmapKey} />
      </Section>

      <Section>
        {members.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-16 grid place-items-center text-center" data-testid="team-empty">
            <UserPlus size={32} className="text-neutral-400 mb-3" />
            <div className="font-display text-xl font-bold mb-1">No team members yet</div>
          </div>
        ) : (
          <div className="border border-neutral-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50/50 text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Role</th>
                  <th className="text-left px-4 py-3 font-semibold">Capacity</th>
                  {isSuperAdmin && <th className="text-left px-4 py-3 font-semibold">Hourly cost</th>}
                  <th className="text-left px-4 py-3 font-semibold">Joined</th>
                  {isAdmin && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`team-row-${m.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-sm bg-neutral-900 text-white grid place-items-center text-[10px] font-semibold">{(m.name || m.email).slice(0, 1).toUpperCase()}</div>
                        <span>{m.name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600 font-mono text-xs">{m.email}</td>
                    <td className="px-4 py-3">
                      {isSuperAdmin && m.id !== me?.id ? (
                        <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                          <SelectTrigger className="h-7 w-32 rounded-sm text-xs" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm ${roleStyle(m.role)}`}>{m.role.replace("_", " ")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3" data-testid={`capacity-cell-${m.id}`}>
                      {editingCapacity === m.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            max="168"
                            value={capacityValue}
                            onChange={(e) => setCapacityValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveCapacity(m.id);
                              if (e.key === "Escape") setEditingCapacity(null);
                            }}
                            autoFocus
                            className="h-7 w-16 rounded-sm font-mono text-xs"
                            data-testid={`capacity-input-${m.id}`}
                          />
                          <Button size="sm" onClick={() => saveCapacity(m.id)} className="h-7 rounded-sm bg-neutral-900 hover:bg-neutral-800 text-xs px-2" data-testid={`capacity-save-${m.id}`}>Save</Button>
                        </div>
                      ) : isAdmin ? (
                        <button
                          onClick={() => startCapacityEdit(m)}
                          className="font-mono text-xs text-neutral-700 hover:text-neutral-900 hover:underline"
                          data-testid={`capacity-edit-${m.id}`}
                        >
                          {m.weekly_capacity_hours ?? 40}h
                        </button>
                      ) : (
                        <span className="font-mono text-xs text-neutral-700">{m.weekly_capacity_hours ?? 40}h</span>
                      )}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3" data-testid={`hourly-cost-cell-${m.id}`}>
                        {editingCost === m.id ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-neutral-500">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={costValue}
                              onChange={(e) => setCostValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveCost(m.id);
                                if (e.key === "Escape") setEditingCost(null);
                              }}
                              autoFocus
                              className="h-7 w-20 rounded-sm font-mono text-xs"
                              data-testid={`hourly-cost-input-${m.id}`}
                            />
                            <Button size="sm" onClick={() => saveCost(m.id)} className="h-7 rounded-sm bg-neutral-900 hover:bg-neutral-800 text-xs px-2" data-testid={`hourly-cost-save-${m.id}`}>Save</Button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startCostEdit(m)}
                            className="font-mono text-xs text-neutral-700 hover:text-neutral-900 hover:underline"
                            data-testid={`hourly-cost-edit-${m.id}`}
                          >
                            ${Number(m.hourly_cost ?? 0).toFixed(2)}/h
                          </button>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-neutral-600 font-mono text-xs">{m.created_at?.slice(0, 10) || "—"}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => openReassignFor(m)} className="rounded-sm h-8" data-testid={`reassign-${m.id}`} title="Bulk reassign their open tasks">
                          <Shuffle size={13} />
                        </Button>
                        {isSuperAdmin && m.id !== me?.id && (
                          <Button variant="ghost" size="sm" onClick={() => openReset(m)} className="rounded-sm h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50" data-testid={`reset-password-${m.id}`} title="Reset this user's password">
                            <KeyRound size={13} />
                          </Button>
                        )}
                        {m.id !== me?.id && (
                          <Button variant="ghost" size="sm" onClick={() => remove(m.id)} className="rounded-sm h-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`remove-member-${m.id}`}><Trash2 size={13} /></Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Dialog open={openReassign} onOpenChange={setOpenReassign}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="reassign-dialog">
          <DialogHeader><DialogTitle>Bulk reassign open tasks</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-neutral-600">
              Move all of <span className="font-semibold text-neutral-900">{reassignFrom?.name || reassignFrom?.email}</span>'s open tasks (todo / in-progress / review) to another teammate.
            </div>
            <div>
              <Label>New assignee</Label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger data-testid="reassign-to-select"><SelectValue placeholder="Pick a teammate" /></SelectTrigger>
                <SelectContent>
                  {members.filter(m => m.id !== reassignFrom?.id).map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doReassign} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="reassign-submit">Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetPassword(""); } }}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-neutral-600">
              Set a new password for <span className="font-semibold text-neutral-900">{resetTarget?.name || resetTarget?.email}</span>.
              Their existing password will stop working immediately. Any login lockout will be cleared.
            </div>
            <div>
              <Label>New password</Label>
              <Input
                type="text"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitReset(); }}
                placeholder="At least 6 characters"
                autoFocus
                className="rounded-sm font-mono"
                data-testid="reset-password-input"
              />
              <div className="text-[11px] text-neutral-500 mt-1">
                The password is shown so you can copy & share it. It will also be copied to your clipboard on save.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setResetPassword(""); }} className="rounded-sm" data-testid="reset-password-cancel">Cancel</Button>
            <Button onClick={submitReset} disabled={resetLoading || resetPassword.trim().length < 6}
                    className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="reset-password-submit">
              {resetLoading ? "Resetting…" : "Reset & copy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
